import { useEffect, useState } from 'react'
import { api } from '../api'
import type { InvoiceSnapshot } from '../../shared/types'
import InvoiceDocument from './InvoiceDocument'
import { pushError } from '../toast'
import { appToday, mmddyy } from '../datetime'

// Invoice generation flow: choose a period (this month / last 3 months / custom
// range), preview the rendered invoice with an editable issue + due date, then
// confirm to freeze it.
export default function InvoiceModal({
  clientId,
  month,
  onClose,
  onGenerated,
}: {
  clientId: number
  month: string
  onClose: () => void
  onGenerated: (invoiceId: number) => void
}) {
  const [y, m] = month.split('-').map(Number)
  const monthEnd = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
  const last3Start = new Date(Date.UTC(y, m - 1 - 2, 1)).toISOString().slice(0, 10)
  const monthLabel = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })

  const [periodMode, setPeriodMode] = useState<'month' | 'last3' | 'custom'>('month')
  const [customStart, setCustomStart] = useState(last3Start)
  const [customEnd, setCustomEnd] = useState(monthEnd)

  const period =
    periodMode === 'month'
      ? { month }
      : periodMode === 'last3'
        ? { start: last3Start, end: monthEnd }
        : { start: customStart, end: customEnd }
  const periodLabel =
    periodMode === 'month' ? monthLabel : `${mmddyy((period as any).start)} – ${mmddyy((period as any).end)}`

  const [issued, setIssued] = useState(() => appToday())
  const [due, setDue] = useState<string>('')
  const [dueTouched, setDueTouched] = useState(false)
  const [attachItemized, setAttachItemized] = useState(false)
  const [snap, setSnap] = useState<InvoiceSnapshot | null>(null)
  const [warnings, setWarnings] = useState<{ no_category: number; zero_duration: number; entry_count: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)

  const body = () => ({
    ...period,
    issued_date: issued,
    due_date: dueTouched && due ? due : undefined,
    attach_itemized: attachItemized,
  })

  async function loadPreview() {
    setLoading(true)
    try {
      const res = await api.previewInvoice(clientId, body())
      setSnap(res.snapshot)
      setWarnings(res.warnings)
      if (!dueTouched && res.snapshot.due_date) setDue(res.snapshot.due_date)
    } catch (e) {
      pushError(`Couldn't build preview: ${(e as Error).message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issued, due, attachItemized, periodMode, customStart, customEnd])

  async function confirm() {
    setGenerating(true)
    try {
      const inv = await api.generateInvoice(clientId, body())
      onGenerated(inv.id)
    } catch (e) {
      pushError(`Generate failed: ${(e as Error).message}`)
      setGenerating(false)
    }
  }

  const hasWarnings = warnings && (warnings.no_category > 0 || warnings.zero_duration > 0)
  const empty = warnings && warnings.entry_count === 0

  return (
    <div className="no-print fixed inset-0 z-40 flex items-start justify-center overflow-auto bg-black/40 p-3 sm:p-6">
      <div className="w-full max-w-3xl rounded-lg bg-slate-100 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
          <h2 className="text-lg font-semibold text-slate-900">Generate invoice — {periodLabel}</h2>
          <button className="text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Period selection */}
        <div className="flex flex-wrap items-end gap-4 border-b border-slate-200 bg-white px-5 py-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-slate-500">Period</span>
            <select
              className="rounded border border-slate-300 px-2 py-1"
              value={periodMode}
              onChange={(e) => setPeriodMode(e.target.value as typeof periodMode)}
            >
              <option value="month">This month ({monthLabel})</option>
              <option value="last3">Last 3 months</option>
              <option value="custom">Custom range</option>
            </select>
          </label>
          {periodMode !== 'month' && (
            <div className="flex items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-slate-500">From</span>
                <CommitDateInput value={(period as any).start} disabled={periodMode === 'last3'} onCommit={setCustomStart} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-slate-500">To</span>
                <CommitDateInput value={(period as any).end} disabled={periodMode === 'last3'} onCommit={setCustomEnd} />
              </label>
            </div>
          )}
        </div>

        {/* Issue / due / options */}
        <div className="flex flex-wrap items-end gap-4 border-b border-slate-200 bg-white px-5 py-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-slate-500">Issue date</span>
            <input type="date" className="tabular rounded border border-slate-300 px-2 py-1" value={issued} onChange={(e) => setIssued(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-500">Due date</span>
            <input
              type="date"
              className="tabular rounded border border-slate-300 px-2 py-1"
              value={due}
              onChange={(e) => {
                setDueTouched(true)
                setDue(e.target.value)
              }}
            />
          </label>
          <label className="flex items-center gap-2 text-slate-600">
            <input type="checkbox" checked={attachItemized} onChange={(e) => setAttachItemized(e.target.checked)} />
            Attach itemized detail (2nd page)
          </label>
          <div className="ml-auto flex gap-2">
            <button className="rounded px-3 py-1.5 text-slate-600 hover:bg-slate-100" onClick={onClose}>
              Cancel
            </button>
            <button
              className="rounded bg-slate-900 px-4 py-1.5 font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              onClick={confirm}
              disabled={generating || loading || !!empty}
            >
              {generating ? 'Generating…' : 'Confirm & freeze invoice'}
            </button>
          </div>
        </div>

        {hasWarnings && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-sm text-amber-800">
            ⚠ This period has{' '}
            {warnings!.no_category > 0 && <b>{warnings!.no_category} entr{warnings!.no_category === 1 ? 'y' : 'ies'} with no category</b>}
            {warnings!.no_category > 0 && warnings!.zero_duration > 0 && ' and '}
            {warnings!.zero_duration > 0 && <b>{warnings!.zero_duration} with zero duration</b>}. You can still proceed.
          </div>
        )}

        <div className="max-h-[70vh] overflow-auto p-5">
          {loading && <p className="text-slate-400">Building preview…</p>}
          {empty && <p className="rounded bg-white p-6 text-center text-slate-500">No uninvoiced entries in this period.</p>}
          {!loading && !empty && snap && <InvoiceDocument snap={snap} />}
        </div>
      </div>
    </div>
  )
}

// Commits on blur / Enter so typing a date doesn't reload the preview mid-entry.
function CommitDateInput({ value, onCommit, disabled }: { value: string; onCommit: (v: string) => void; disabled?: boolean }) {
  const [v, setV] = useState(value)
  useEffect(() => setV(value), [value])
  return (
    <input
      type="date"
      disabled={disabled}
      className="tabular rounded border border-slate-300 px-2 py-1 disabled:bg-slate-50 disabled:text-slate-400"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v && v !== value && onCommit(v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
    />
  )
}
